import { FC } from 'react';
import { Button, Dropdown, InformedField, Modal, TextField } from '@components';
import { Form, FormApi, FormState, useFormState } from 'informed';
import { useGlobalContext, useRepository } from '@context';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import {
  IDropdownOption,
  IUsersOptions,
  Nullable,
  UserCreationPayload,
  UserDefault,
  UserPatchPayload,
} from '@common/interfaces';
import css from './styles.module.scss';

interface IUserModal {
  closeModal: () => void;
  userData: Nullable<UserDefault>;
}

interface UserFormValues {
  admin_permission: Nullable<IDropdownOption>;
  customer_area: Nullable<IDropdownOption>;
  email: string;
  facilities: IDropdownOption[];
  facility_permissions: IDropdownOption[];
  first_name: string;
  last_name: string;
}

enum AdminPermissions {
  DO_SUPERUSER = 'do_superuser',
  DO_MANAGER = 'do_manager',
  NO_PERMISSIONS = 'no_permissions',
}

const permissionOptions = [
  { label: 'Digital Office Superuser', value: AdminPermissions.DO_SUPERUSER },
  { label: 'Digital Office Manager', value: AdminPermissions.DO_MANAGER },
  { label: 'No permissions', value: AdminPermissions.NO_PERMISSIONS },
];

const FacilityPermissionField = () => {
  const { values } = useFormState();

  const { admin_permission, facilities } = values as unknown as UserFormValues;

  if (admin_permission?.value !== AdminPermissions.DO_MANAGER) {
    return null;
  }

  const isFacilitiesPresent = facilities.length > 0;

  return (
    <InformedField label='Digital Office Manager permission'>
      <Dropdown
        placeholder={
          !isFacilitiesPresent ? 'Please select at least one facility first' : 'Select one or multiple options'
        }
        isDisabled={!isFacilitiesPresent}
        name='facility_permissions'
        options={facilities}
        variant='dark'
        isMulti
        isClearable
      />
    </InformedField>
  );
};

const UserModal: FC<IUserModal> = ({ closeModal, userData }) => {
  const {
    user: { is_do_superuser },
  } = useGlobalContext();

  const isNew = !userData;

  const { accountRepository } = useRepository();

  const { data: options, isLoading: isOptionsLoading } = useQuery<IUsersOptions>('user-options', () =>
    accountRepository.getUsersOptions()
  );

  const { mutate: createUser, isLoading: inCreationUserProgress } = useMutation(
    (data: UserCreationPayload) => accountRepository.createUser(data),
    {
      onSuccess: () => {
        toast.success('User creating request was sent');
        closeModal();
      },
    }
  );

  const { mutate: patchUser, isLoading: isPatchingUserProgress } = useMutation(
    (data: UserPatchPayload) => accountRepository.patchUser(userData?.uuid || '', data),
    {
      onSuccess: () => {
        toast.success('User updating request was sent');
        closeModal();
      },
    }
  );

  const onSubmit = ({ values }: FormState) => {
    const { admin_permission, customer_area, email, facilities, facility_permissions, first_name, last_name } =
      values as unknown as UserFormValues;

    const selectedFacilityIds = facility_permissions
      ? new Set(facility_permissions.map(({ value }) => value))
      : new Set();
    const baseFormData = {
      do_superuser: admin_permission?.value === AdminPermissions.DO_SUPERUSER,
      facility_permissions: facilities.map(({ value }) => ({
        id: Number(value),
        do_manager: is_do_superuser ? selectedFacilityIds.has(value) : undefined,
      })),
    };

    if (isNew) {
      createUser({
        ...baseFormData,
        email,
        user_location: customer_area?.value,
      });
    } else {
      patchUser({
        ...baseFormData,
        first_name,
        last_name,
        customer_area_location_id: customer_area?.value,
      });
    }
  };

  const yupSchema = Yup.object().shape({
    admin_permission: Yup.object(),
    customer_area: Yup.object(),
    email: Yup.string().when([], {
      is: () => isNew === true,
      then: (schema) => schema.email('Invalid email').required('Required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    facilities: Yup.array().of(
      Yup.object({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
    ),
    facility_permissions: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .when('admin_permission', {
        is: (admin_permission: IDropdownOption) => admin_permission?.value === AdminPermissions.DO_MANAGER,
        then: (schema) => schema.min(1, 'Select at least one facility').required('Facility permissions are required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    first_name: Yup.string(),
    last_name: Yup.string(),
  });

  const currentUserFacilities = userData?.facilities.map(({ facility_id_on_hbh }) => facility_id_on_hbh) || [];
  const currentUserManagerFacilities =
    userData?.facilities.filter(({ do_manager }) => do_manager).map(({ facility_id_on_hbh }) => facility_id_on_hbh) ||
    [];

  const initialValues = {
    admin_permission: isNew
      ? null
      : userData?.do_superuser
      ? permissionOptions.find(({ value }) => value === AdminPermissions.DO_SUPERUSER)
      : currentUserManagerFacilities.length > 0
      ? permissionOptions.find(({ value }) => value === AdminPermissions.DO_MANAGER)
      : permissionOptions.find(({ value }) => value === AdminPermissions.NO_PERMISSIONS),
    customer_area:
      options?.customer_areas.find(({ value }) => value === userData?.customer_area_location?.uuid) || null,
    email: userData ? userData.email : '',
    facilities: options?.facilities.filter(({ value }) => currentUserFacilities.includes(value)) || [],
    facility_permissions: options?.facilities.filter(({ value }) => currentUserManagerFacilities.includes(value)) || [],
    first_name: userData ? userData.first_name : '',
    last_name: userData ? userData.last_name : '',
  };

  const loading = isOptionsLoading || inCreationUserProgress || isPatchingUserProgress;

  return (
    <Modal
      variant='dark'
      className={css.modal}
      title={isNew ? 'Create new user' : 'Edit user'}
      closeModal={closeModal}
      loading={loading}
    >
      {options ? (
        <Form onSubmit={onSubmit} yupSchema={yupSchema} initialValues={initialValues}>
          {({ formApi }: { formApi: FormApi }) => (
            <>
              <Modal.Content>
                <div className={css.form}>
                  {userData ? (
                    <div className={css.info}>
                      <div className={css.row}>
                        <div className={css.label}>User email address</div>
                        <div className={css.value}>{userData.email}</div>
                      </div>
                      <div className={css.row}>
                        <div className={css.label}>Organisation</div>
                        <div className={css.value}>{userData.organisation?.short_name || '-'}</div>
                      </div>
                    </div>
                  ) : null}
                  {isNew ? (
                    <InformedField label='Email address *'>
                      <TextField placeholder='Enter email address' variant='dark' name='email' autoComplete='off' />
                    </InformedField>
                  ) : (
                    <>
                      <InformedField label='First name'>
                        <TextField placeholder='Enter first name' variant='dark' name='first_name' autoComplete='off' />
                      </InformedField>
                      <InformedField label='Last name'>
                        <TextField placeholder='Enter last name' variant='dark' name='last_name' autoComplete='off' />
                      </InformedField>
                    </>
                  )}
                  <InformedField label='Digital Office'>
                    <Dropdown
                      placeholder='Select one or multiple options'
                      name='facilities'
                      options={options.facilities}
                      variant='dark'
                      isMulti
                      isClearable
                      onChange={() => formApi.setValue('facility_permissions', [])}
                    />
                  </InformedField>
                  {is_do_superuser ? (
                    <>
                      <InformedField label='Customer Area'>
                        <Dropdown
                          placeholder='Select customer area'
                          name='customer_area'
                          options={options.customer_areas}
                          variant='dark'
                          isClearable
                        />
                      </InformedField>
                      <InformedField label='Admin permission'>
                        <Dropdown
                          placeholder='Select admin permission'
                          name='admin_permission'
                          options={permissionOptions}
                          variant='dark'
                        />
                      </InformedField>
                      <FacilityPermissionField />
                    </>
                  ) : null}
                </div>
              </Modal.Content>
              <Modal.Footer>
                <Button text='Cancel' variant='transparent' onClick={closeModal} />
                {isNew ? (
                  <Button text='Confirm and create user' type='submit' />
                ) : (
                  <Button text='Confirm and update user' variant='forest' type='submit' />
                )}
              </Modal.Footer>
            </>
          )}
        </Form>
      ) : null}
    </Modal>
  );
};

export default UserModal;
